"use client";

import { useContext, useCallback } from "react";
import { MedGroupCard } from "@/components/meds/med-group-card/med-group-card";
import { SimpleMedGroupCard } from "@/components/meds/atc/atc-list/simple-med-group-card/simple-med-group-card";
import { MedGroupListSkeleton } from "./med-group-skeleton";
import { AtcMedGroupsPagination } from "./atc-med-groups-pagination";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { LogView } from "@/components/meds/meds-search/meds-list/log-view/log-view";

interface AtcMedGroupsListProps {
  loading: boolean;
  paginatedMedGroups: MedGroupPreviewVM[];
  compactView: boolean;
  search: string;
  page: number;
  totalPages: number;
  totalItems: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export const AtcMedGroupsList = ({
  loading,
  paginatedMedGroups,
  compactView,
  search,
  page,
  totalPages,
  totalItems,
  onPreviousPage,
  onNextPage,
}: AtcMedGroupsListProps) => {
  const { logView } = useContext(ResourceContext);

  const handleMedGroupNavigate = useCallback(
    (medGroup: MedGroupPreviewVM) => {
      logEvent("atc_med_group_click", logView, {
        resourceTitle: medGroup.superGroup?.title ?? medGroup.title,
        medGroupId: medGroup.id,
      });
    },
    [logView],
  );

  if (loading) {
    return <MedGroupListSkeleton count={compactView ? 20 : 5} />;
  }

  if (paginatedMedGroups.length === 0) {
    return (
      <p className="text-center text-gray-500">
        {search
          ? `Brak wyników dla „${search}"`
          : "Brak dostępnych leków dla tego ATC"}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <LogView medGroups={paginatedMedGroups} query={search} />

      <div className={`flex flex-col ${compactView ? "gap-0" : "gap-4"}`}>
        {paginatedMedGroups.map((medGroup) =>
          compactView ? (
            <SimpleMedGroupCard
              key={medGroup.id}
              medGroup={medGroup}
              onNavigate={() => handleMedGroupNavigate(medGroup)}
            />
          ) : (
            <MedGroupCard
              key={medGroup.id}
              medGroup={medGroup}
              onNavigate={() => handleMedGroupNavigate(medGroup)}
            />
          ),
        )}
      </div>
      <AtcMedGroupsPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onPrevious={onPreviousPage}
        onNext={onNextPage}
      />
    </div>
  );
};
